import { applyWellpreptChatBranding, type wellpreptChatBranding } from "@healthlinks/ui/theme/agent-chat-branding";
import { Box, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AGENT_CHAT_API_URL,
  AGENT_CHAT_INSTALLATION_ID,
  AGENT_CHAT_WIDGET_URL,
  API_BASE_URL,
} from "@/config/env";

interface WidgetToken {
  token: string;
  expiresAt?: string;
}

interface AgentChatElement extends HTMLElement {
  branding: typeof wellpreptChatBranding;
  showActionDetails: boolean;
  maximizeX: number;
  platformUrl: string;
  installationId: string;
  mode: "floating" | "inline";
  closeOnHostActionSuccess: boolean;
  getToken?: () => Promise<WidgetToken | string>;
  onHostAction?: (detail: AgentChatHostActionDetail) => AgentChatHostState | void;
}

interface AgentChatHostAction {
  name: string;
  arguments: Record<string, unknown>;
}

interface AgentChatHostActionDetail {
  action: AgentChatHostAction;
}

interface AgentChatHostState {
  name: string;
  arguments: Record<string, unknown>;
}

interface HostNavigation {
  path: string;
  state: AgentChatHostState;
}

function positiveId(value: unknown): string | null {
  if (typeof value === "number" && Number.isSafeInteger(value) && value > 0) {
    return String(value);
  }
  if (typeof value === "string" && /^\d+$/.test(value) && Number(value) > 0) {
    return value;
  }
  return null;
}

export function resolveWellpreptHostAction(
  action: AgentChatHostAction,
): HostNavigation | null {
  if (action.name === "host.user.view") {
    const userId = positiveId(action.arguments.user_id);
    return userId
      ? {
          path: `/users/#/${encodeURIComponent(userId)}/view`,
          state: { name: "user.details", arguments: { user_id: Number(userId) } },
        }
      : null;
  }

  if (action.name === "host.organization.view") {
    const organizationId = positiveId(action.arguments.organization_id);
    return organizationId
      ? {
          path: `/organizations/#/${encodeURIComponent(organizationId)}/view`,
          state: {
            name: "organization.details",
            arguments: { organization_id: Number(organizationId) },
          },
        }
      : null;
  }

  return null;
}

export function createWellpreptHostActionHandler(
  navigate: (path: string) => void,
): NonNullable<AgentChatElement["onHostAction"]> {
  return ({ action }) => {
    const navigation = resolveWellpreptHostAction(action);
    if (!navigation) return;
    navigate(navigation.path);
    return navigation.state;
  };
}

let widgetLoader: Promise<void> | undefined;

export function loadAgentChatWidget(sourceUrl: string): Promise<void> {
  if (customElements.get("agent-chat")) {
    return Promise.resolve();
  }
  if (!widgetLoader) {
    widgetLoader = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.type = "module";
      script.src = sourceUrl;
      script.addEventListener("load", () => resolve(), { once: true });
      script.addEventListener(
        "error",
        () => reject(new Error("Agent Chat widget could not be loaded")),
        { once: true },
      );
      document.head.append(script);
    }).catch((error) => {
      widgetLoader = undefined;
      throw error;
    });
  }
  return widgetLoader!;
}

export async function getAgentChatToken(
  fetcher: typeof fetch = fetch,
): Promise<WidgetToken> {
  const currentToken = localStorage.getItem("token");
  if (!currentToken) {
    throw new Error("The current admin session is unavailable");
  }

  const response = await fetcher(`${API_BASE_URL}/admin/api/agent-chat/token`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${currentToken}`,
    },
  });
  if (!response.ok) {
    throw new Error(`Agent Chat authorization failed (${response.status})`);
  }

  const payload = (await response.json()) as Partial<WidgetToken>;
  if (typeof payload.token !== "string" || !payload.token.trim()) {
    throw new Error("Agent Chat authorization returned an invalid token");
  }
  return { token: payload.token, expiresAt: payload.expiresAt };
}

export default function AgentChatWidget({ maximizeX = 0 }: { maximizeX?: number }) {
  const elementRef = useRef<AgentChatElement | null>(null);
  const maximizeXRef = useRef(maximizeX);
  maximizeXRef.current = maximizeX;

  useEffect(() => {
    if (elementRef.current) elementRef.current.maximizeX = maximizeX;
  }, [maximizeX]);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [loadError, setLoadError] = useState(false);
  const configured = Boolean(
    AGENT_CHAT_API_URL && AGENT_CHAT_WIDGET_URL && AGENT_CHAT_INSTALLATION_ID,
  );

  useEffect(() => {
    if (!configured) return;

    let active = true;
    let element: AgentChatElement | undefined;
    void loadAgentChatWidget(AGENT_CHAT_WIDGET_URL)
      .then(() => {
        if (!active) return;
        element = document.createElement("agent-chat") as AgentChatElement;
        elementRef.current = element;
        element.maximizeX = maximizeXRef.current;
        element.platformUrl = AGENT_CHAT_API_URL;
        element.installationId = AGENT_CHAT_INSTALLATION_ID;
        element.mode = "floating";
        applyWellpreptChatBranding(element);
        element.closeOnHostActionSuccess = true;
        element.getToken = getAgentChatToken;
        element.onHostAction = createWellpreptHostActionHandler(
          (path) => navigateRef.current(path),
        );
        document.body.append(element);
      })
      .catch(() => {
        if (active) setLoadError(true);
      });

    return () => {
      active = false;
      element?.remove();
      elementRef.current = null;
    };
  }, [configured]);

  if (!configured || !loadError) return null;

  return (
    <Box
      position="fixed"
      right="4"
      bottom="4"
      zIndex="toast"
      rounded="md"
      bg="red.600"
      color="white"
      px="4"
      py="3"
      role="status"
    >
      <Text fontSize="sm">Agent Chat is temporarily unavailable.</Text>
    </Box>
  );
}
